import React, { useContext, useEffect, useRef, useState } from "react";

import {
  IAuthorizedDiscountComponentProps,
  IAuthorizedDiscountComponentRef,
  IDiscountResponse,
  IExtendedDiscountItem,
} from "./AuthorizedDiscountComponent.types";
import { CoherencePanel } from "@coherence-design-system/controls";
import {
  ADD_ACTION,
  DefaultEssentialBundleDiscount,
  DiscountListItem,
  DiscountTypes,
  InitialStateOfResponseMessage,
} from "../../Common/QuotationData";
import {
  OverridePanelPaddingStyle,
  SeparatorStyles,
  buttonStyleWithWidth100,
} from "../../QuotationStyle";
import {
  ChoiceGroup,
  DefaultButton,
  IChoiceGroupOption,
  IComboBoxOption,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Separator,
  Stack,
} from "@fluentui/react";
import { QuotationService } from "../../../../services/Quotations/QuotationService";
import { IResponseMessage, QuotationInfoType } from "../../Quotation.types";
import { QuotationContext } from "../../QuotationContext";
import { dateDifferenceInDays } from "../../../../Utils/Date";
import { decimalValueRegex, wholeNumberRegex } from "../../../../Utils/Common";
import { AuthorizedDiscountAmountComponent } from "../AuthorizedDiscountAmountComponent/AuthorizedDiscountAmountComponent";
import { AuthorizedDiscountPercentageComponent } from "../AuthorizedDiscountPercentageComponent/AuthorizedDiscountPercentageComponent";
import { DeviceType } from "../../../../common/enum/DeviceTypes";

export const onErrorCapture = (
  discountSuccessMessage: IResponseMessage,
  setDiscountSuccessMessage: React.Dispatch<
    React.SetStateAction<IResponseMessage>
  >,
  discountFailureMessage: IResponseMessage,
  setDiscountFailureMessage: React.Dispatch<
    React.SetStateAction<IResponseMessage>
  >,
  discountInfoMessage: IResponseMessage,
  setDiscountInfoMessage: React.Dispatch<
    React.SetStateAction<IResponseMessage>
  >
) => {
  return (
    <Stack>
      {discountSuccessMessage.showMessage && (
        <MessageBar
          messageBarType={MessageBarType.success}
          onDismiss={() =>
            setDiscountSuccessMessage(InitialStateOfResponseMessage)
          }
        >
          {discountSuccessMessage.message}
        </MessageBar>
      )}
      {discountFailureMessage.showMessage && (
        <MessageBar
          messageBarType={MessageBarType.error}
          onDismiss={() =>
            setDiscountFailureMessage(InitialStateOfResponseMessage)
          }
        >
          {discountFailureMessage.message}
        </MessageBar>
      )}
      {discountInfoMessage.showMessage && (
        <MessageBar
          messageBarType={MessageBarType.info} className="waringMessage"
          onDismiss={() =>
            setDiscountInfoMessage(InitialStateOfResponseMessage)
          }
        >
          {discountInfoMessage.message}
        </MessageBar>
      )}
    </Stack>
  );
};
export const AuthorizedDiscountComponent: React.FunctionComponent<
  IAuthorizedDiscountComponentProps
> = (props) => {
  const {
    selectedPivot,
    isDiscountPanelOpen,
    dismissDiscountPanel,
    selectedProduct,
    selectedProductIndex,
    selection,
    setLoading,
  } = props;
  const amountComponentRef = useRef<IAuthorizedDiscountComponentRef>(null);
  const percentageComponentRef = useRef<IAuthorizedDiscountComponentRef>(null);
  const [discountData, setDiscountData] = useState<IDiscountResponse[]>([]);
  const [discountReasons, setDiscountReasons] = useState<IComboBoxOption[]>([]);
  const [showAlert, setShowAlert] = useState(false);

  const [discountAmountList, setDiscountAmountList] = useState<
    IExtendedDiscountItem[]
  >([]);
  const [discountPercentageList, setDiscountPercentageList] = useState<
    IExtendedDiscountItem[]
  >([]);
  const [selectedDiscount, setSelectedDiscount] = useState<string>(
    selectedProduct.authDiscountType ?? DiscountTypes[0].key
  );
  const { quotationInfo } = useContext(QuotationContext) as QuotationInfoType;
  useEffect(() => {
    getCustomerManualDiscount();
  }, []);
  const getDiscountsOfSelectedProduct = () => {
    if (
      selectedDiscount === DiscountTypes[1].key &&
      selectedProduct?.authDiscountAmounts?.length > 0
    ) {
      const discounts = selectedProduct?.authDiscountAmounts?.map((d) => {
        const dateDifference: number = dateDifferenceInDays(
          new Date(d.expirationDate),
          new Date()
        );
        return {
          ...DiscountListItem,
          isEditable: true,
          expirationDate: d.expirationDate,
          authorizedDiscountAmount: d.authorizedDiscountAmount,
          authorizedDiscountDecimalError: !((quotationInfo.customer?.country === "Japan") ? 
            wholeNumberRegex.test(d.authorizedDiscountAmount.toString()) : 
            decimalValueRegex.test(d.authorizedDiscountAmount.toString())
          ),
          minimumAuthorizedDiscountError:
            typeof d.authorizedDiscountAmount === "number" &&
            isValidMinimumDiscount(
              d.authorizedDiscountAmount,
              d.minimumAuthorizedDiscountAmount
            ),
          authorizedDiscountReason: d.authorizedDiscountReason,
          authorizedDiscountDateError: dateDifference > 30 ? true : false,
          authorizedDiscountDateWarning:
            dateDifference >= 0 && dateDifference <= 30 ? true : false,
          authorizedDiscountReasonObj: {
            key: d.authorizedDiscountReason,
            text: d.authorizedDiscountReason,
          },
          minimumAuthorizedDiscountAmount: Number(
            d.minimumAuthorizedDiscountAmount
          ),
        };
      });
      setDiscountAmountList(discounts);
    } else if (
      selectedDiscount === DiscountTypes[0].key &&
      selectedProduct?.authDiscountAmounts?.length > 0
    ) {
      const discounts = selectedProduct?.authDiscountAmounts?.map((d) => {
        const dateDifference: number = dateDifferenceInDays(
          new Date(d.expirationDate),
          new Date()
        );
        return {
          ...DiscountListItem,
          isEditable: true,
          expirationDate: d.expirationDate,
          authorizedDiscountPercentage: d.authorizedDiscountPercentage,
          authorizedDiscountDecimalError: !((quotationInfo.customer?.country === "Japan") ? 
            wholeNumberRegex.test(d.authorizedDiscountPercentage.toString()) : 
            decimalValueRegex.test(d.authorizedDiscountPercentage.toString())
          ),
          minimumAuthorizedDiscountError:
            typeof d.authorizedDiscountPercentage === "number" && d.authorizedDiscountPercentage < d.minimumAuthorizedDiscountAmount,
          authorizedDiscountReason: d.authorizedDiscountReason,
          authorizedDiscountDateError: dateDifference > 30 ? true : false,
          authorizedDiscountDateWarning:
            dateDifference >= 0 && dateDifference <= 30 ? true : false,
          authorizedDiscountReasonObj: {
            key: d.authorizedDiscountReason,
            text: d.authorizedDiscountReason,
          },
          minimumAuthorizedDiscountAmount: Number(
            d.minimumAuthorizedDiscountAmount
          ),
        };
      });
      setDiscountPercentageList(discounts);
    }
  };
  const renderDiscountTypeSelection = () => {
    return (
      <ChoiceGroup
        defaultSelectedKey={selectedDiscount}
        options={DiscountTypes}
        styles={{
          flexContainer: {
            display: "flex",
            "& > div": {
              paddingRight: "10px",
            },
          },
        }}
        onChange={(
          ev?: React.FormEvent<HTMLInputElement | HTMLElement> | undefined,
          option?: IChoiceGroupOption | undefined
        ) => _onChangeDiscountSelection(ev, option)}
        label="Select"
        required={true}
      />
    );
  };
  const _onChangeDiscountSelection = (
    ev?: React.FormEvent<HTMLInputElement | HTMLElement> | undefined,
    option?: IChoiceGroupOption | undefined
  ) => {
    option && setSelectedDiscount(option?.key);
  };
  const getCustomerManualDiscount = () => {
    if (quotationInfo.customer.id) {
      QuotationService.getCustomerManualDiscount(quotationInfo.customer.id)
        .then((res) => {
          if (res.data.code === 200 && res.data.data) {
            getDiscountsOfSelectedProduct();
            const response = [...res.data.data];
            if(selectedPivot !== DeviceType.Standalone && quotationInfo.bundleItems.length > 0) {
              response.push(DefaultEssentialBundleDiscount);
            }            
            setDiscountData(response);
            if(response.length == 0) {
              setShowAlert(true);
            }
            const reasons = response.map((item: IDiscountResponse) => {
              return {
                key: item.contractSelected,
                text: item.contractSelected,
              };
            });
            
            // Check if customer has deal registration and add Deal Registration Booster
            const dealRegistrationDiscount = response.find(
              (item: IDiscountResponse) => item.contractSelected === "Deal Registration"
            );
            
            if (dealRegistrationDiscount) {
              // Add Deal Registration Booster as an additional option
              reasons.push({
                key: "Deal Registration Booster",
                text: "Deal Registration Booster",
              });
            }
            
            if (reasons.length) {
              setDiscountReasons(reasons);
            }
          } else {
            setDiscountData([DefaultEssentialBundleDiscount]);
            setDiscountReasons([
              {
                key: DefaultEssentialBundleDiscount.contractSelected,
                text: DefaultEssentialBundleDiscount.contractSelected,
              },
            ]);
          }
        })
        .catch((error) => {
          setDiscountData([DefaultEssentialBundleDiscount]);
          setDiscountReasons([
            {
              key: DefaultEssentialBundleDiscount.contractSelected,
              text: DefaultEssentialBundleDiscount.contractSelected,
            },
          ]);
        });
    }
  };
  const isValidMinimumDiscount = (
    value: number | string,
    minimumDiscountAmount: number | string
  ) => {
    let minimumDiscount = (
      selectedProduct.msrp *
      selectedProduct.quantity *
      (parseFloat(minimumDiscountAmount.toString()) / 100)
    ).toFixed(2);
    return Number(value) < Number(minimumDiscount);
  };
  const onClickApply = () => {
    if (
      selectedDiscount === DiscountTypes[0].key &&
      percentageComponentRef.current
    ) {
      percentageComponentRef.current.onClickApplyDiscount();
    } else if (
      selectedDiscount === DiscountTypes[1].key &&
      amountComponentRef.current
    ) {
      amountComponentRef.current.onClickApplyDiscount();
    }
  };
  const onRenderFooter = () => {
    return (
      <Stack
        verticalFill
        verticalAlign="center"
        style={{ width: "100%", padding: 0 }}
      >
        <Separator styles={SeparatorStyles} />
        {selectedDiscount === DiscountTypes[0].key ? (
          <Stack horizontal horizontalAlign="start">
            <PrimaryButton
              text="Apply"
              type="button"
              onClick={onClickApply}
              style={buttonStyleWithWidth100}
              disabled={
                !discountPercentageList?.length &&
                !selectedProduct?.authDiscountAmounts?.length
              }
            />
            <DefaultButton
              text="Cancel"
              type="button"
              onClick={dismissDiscountPanel}
              style={buttonStyleWithWidth100}
            />
          </Stack>
        ) : (
          <Stack horizontal horizontalAlign="start">
            <PrimaryButton
              text="Apply"
              type="button"
              onClick={onClickApply}
              style={buttonStyleWithWidth100}
              disabled={
                !discountAmountList?.length &&
                !selectedProduct?.authDiscountAmounts?.length
              }
            />
            <DefaultButton
              text="Cancel"
              type="button"
              onClick={dismissDiscountPanel}
              style={buttonStyleWithWidth100}
            />
          </Stack>
        )}
      </Stack>
    );
  };
  return (
    <CoherencePanel
      panelSize="500px"
      headerText={`${ADD_ACTION} Authorized Discount`}
      titleText={`${ADD_ACTION} Authorized Discount`}
      isOpen={isDiscountPanelOpen}
      onDismiss={dismissDiscountPanel}
      onAbort={dismissDiscountPanel}
      onRenderFooterContent={onRenderFooter}
      onRenderFooter={onRenderFooter}
      isFooterAtBottom={true}
      styles={OverridePanelPaddingStyle}
      // You MUST provide this prop! Otherwise screen readers will just say "button" with no label.
      closeButtonAriaLabel="Close"
    >
      <Stack>{renderDiscountTypeSelection()}</Stack>
      {selectedDiscount === DiscountTypes[0].key && (
        <AuthorizedDiscountPercentageComponent
          ref={percentageComponentRef}
          discountData={discountData}
          discountReasons={discountReasons}
          discountList={discountPercentageList}
          setDiscountList={setDiscountPercentageList}
          selectedPivot={selectedPivot}
          dismissDiscountPanel={dismissDiscountPanel}
          selectedProduct={selectedProduct}
          selectedProductIndex={selectedProductIndex}
          selection={selection}
          setLoading={setLoading}
          showAlert={showAlert}
        />
      )}
      {selectedDiscount === DiscountTypes[1].key && (
        <AuthorizedDiscountAmountComponent
          ref={amountComponentRef}
          discountData={discountData}
          discountReasons={discountReasons}
          discountList={discountAmountList}
          setDiscountList={setDiscountAmountList}
          selectedPivot={selectedPivot}
          dismissDiscountPanel={dismissDiscountPanel}
          selectedProduct={selectedProduct}
          selectedProductIndex={selectedProductIndex}
          selection={selection}
          setLoading={setLoading}
          showAlert={showAlert}
        />
      )}
    </CoherencePanel>
  );
};
